import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { InputHTMLAttributes } from 'react';
import { parseFlexibleDate } from 'utils/dateUtils';
import styles from './DatePicker.module.scss';

type BaseInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'value' | 'onChange' | 'readOnly'
>;

export interface DatePickerProps extends BaseInputProps {
  value?: string;
  onChange?: (value: string) => void;
  onValueChange?: (value: string) => void;
  /** When true, shows Today and Clear buttons at the bottom of the calendar popover */
  showActions?: boolean;
  /** Class name applied to the root wrapper (trigger + input container). Use e.g. to set width: 100% or max-width. */
  wrapperClassName?: string;
  minDate?: string;
  /** When true, opens the calendar popover immediately on mount */
  autoOpen?: boolean;
}

function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function parseDate(value?: string): Date | undefined {
  return parseFlexibleDate(value);
}

/** YYYY-MM-DD → mm/dd/yyyy for display */
function toDisplay(value?: string): string {
  const parsed = parseDate(value);
  if (!parsed) return '';
  const m = `${parsed.getMonth() + 1}`.padStart(2, '0');
  const d = `${parsed.getDate()}`.padStart(2, '0');
  const y = parsed.getFullYear();
  return `${m}/${d}/${y}`;
}

/** mm/dd/yyyy → YYYY-MM-DD if valid; year must be 4 digits max */
function parseDisplay(display: string): string | undefined {
  const cleaned = display.replace(/\D/g, '');
  if (cleaned.length !== 8) return undefined;
  const mm = parseInt(cleaned.slice(0, 2), 10);
  const dd = parseInt(cleaned.slice(2, 4), 10);
  const yyyy = parseInt(cleaned.slice(4, 8), 10);
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return undefined;
  const date = new Date(yyyy, mm - 1, dd);
  if (Number.isNaN(date.getTime())) return undefined;
  if (date.getMonth() !== mm - 1 || date.getDate() !== dd) return undefined;
  return formatDate(date);
}

/** From raw digit string build mm/dd/yyyy (max 8 digits, year max 4) */
function formatDisplayDigits(digits: string): string {
  const d = digits.slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4, 8)}`;
}

export default function DatePicker({
  value,
  onChange,
  onValueChange,
  placeholder = 'mm/dd/yyyy',
  className,
  showActions = false,
  wrapperClassName,
  minDate,
  autoOpen = false,
  ...inputProps
}: DatePickerProps) {
  const parsedValue = useMemo(() => parseDate(value), [value]);
  const [open, setOpen] = useState(autoOpen);
  const [view, setView] = useState<'day' | 'month' | 'year'>('day');
  const [visibleMonth, setVisibleMonth] = useState<Date>(
    parsedValue ?? new Date(),
  );
  const [inputFocused, setInputFocused] = useState(false);
  const [localDisplay, setLocalDisplay] = useState('');

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const triggerButtonRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [popoverStyle, setPopoverStyle] = useState<{
    top?: number;
    left?: number;
  }>({});

  const parsedMinDate = useMemo(() => parseDate(minDate), [minDate]);

  useLayoutEffect(() => {
    if (!open || !popoverRef.current) return;
    const anchor = triggerButtonRef.current ?? wrapperRef.current;
    if (!anchor) return;
    const anchorRect = anchor.getBoundingClientRect();
    const popoverWidth = 240;
    const padding = 8;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    let left = triggerButtonRef.current
      ? anchorRect.right - popoverWidth
      : anchorRect.left;
    left = Math.max(
      padding,
      Math.min(left, viewportWidth - popoverWidth - padding),
    );
    let top = anchorRect.bottom + padding;
    if (top + 320 > viewportHeight - padding) {
      top = Math.max(padding, anchorRect.top - 320 - padding);
    }
    setPopoverStyle({ top, left });
  }, [open]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      const inWrapper = wrapperRef.current?.contains(target);
      const inPopover = popoverRef.current?.contains(target);
      if (!inWrapper && !inPopover) {
        setOpen(false);
      }
    }

    if (open) {
      window.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      window.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  useEffect(() => {
    if (parsedValue) {
      setVisibleMonth(parsedValue);
    }
  }, [parsedValue]);

  const startOfMonth = useMemo(
    () => new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), 1),
    [visibleMonth],
  );
  const startDay = startOfMonth.getDay();
  const daysInMonth = useMemo(
    () =>
      new Date(
        visibleMonth.getFullYear(),
        visibleMonth.getMonth() + 1,
        0,
      ).getDate(),
    [visibleMonth],
  );

  const days: Date[] = useMemo(() => {
    const result: Date[] = [];
    for (let i = 0; i < startDay; i += 1) {
      const date = new Date(
        visibleMonth.getFullYear(),
        visibleMonth.getMonth(),
        i - startDay + 1,
      );
      result.push(date);
    }
    for (let day = 1; day <= daysInMonth; day += 1) {
      result.push(
        new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), day),
      );
    }
    while (result.length % 7 !== 0) {
      const last = result[result.length - 1];
      const date = new Date(
        last.getFullYear(),
        last.getMonth(),
        last.getDate() + 1,
      );
      result.push(date);
    }
    return result;
  }, [visibleMonth, startDay, daysInMonth]);

  const today = useMemo(() => new Date(), []);

  const handleSelectDate = (date: Date) => {
    const formatted = formatDate(date);
    onChange?.(formatted);
    onValueChange?.(formatted);
    setOpen(false);
  };

  const handlePrev = () => {
    setVisibleMonth((prev) =>
      view === 'day'
        ? new Date(prev.getFullYear(), prev.getMonth() - 1, 1)
        : view === 'month'
          ? new Date(prev.getFullYear() - 1, prev.getMonth(), 1)
          : new Date(prev.getFullYear() - 12, prev.getMonth(), 1),
    );
  };

  const handleNext = () => {
    setVisibleMonth((prev) =>
      view === 'day'
        ? new Date(prev.getFullYear(), prev.getMonth() + 1, 1)
        : view === 'month'
          ? new Date(prev.getFullYear() + 1, prev.getMonth(), 1)
          : new Date(prev.getFullYear() + 12, prev.getMonth(), 1),
    );
  };

  const monthName = visibleMonth.toLocaleString('default', {
    month: 'long',
  });
  const yearNumber = visibleMonth.getFullYear();

  const years = useMemo(() => {
    const startYear = yearNumber - 6;
    return Array.from({ length: 12 }, (_, index) => startYear + index);
  }, [yearNumber]);

  const handleToggleOpen = () => {
    if (!open) {
      setView('day');
      setVisibleMonth(parsedValue ?? new Date());
    }
    setOpen((prev) => !prev);
  };

  const inputValue = inputFocused ? localDisplay : toDisplay(value);

  const handleInputFocus = () => {
    setInputFocused(true);
    setLocalDisplay(toDisplay(value) || '');
  };

  const handleInputBlur = () => {
    setInputFocused(false);
    const parsed = parseDisplay(localDisplay);
    if (parsed) {
      onChange?.(parsed);
      onValueChange?.(parsed);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 8);
    setLocalDisplay(formatDisplayDigits(raw));
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    }
  };

  const handleToday = () => {
    const todayStr = formatDate(new Date());
    onChange?.(todayStr);
    onValueChange?.(todayStr);
    setVisibleMonth(new Date());
    setOpen(false);
  };

  const handleClear = () => {
    onChange?.('');
    onValueChange?.('');
    setOpen(false);
  };

  return (
    <div
      className={[styles.wrapper, className, wrapperClassName]
        .filter(Boolean)
        .join(' ')}
      ref={wrapperRef}
    >
      <div className={styles.trigger}>
        <input
          {...inputProps}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={inputValue}
          placeholder={placeholder}
          className={styles.input}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          aria-label={placeholder}
        />
        <button
          ref={triggerButtonRef}
          type="button"
          className={styles.iconButton}
          onClick={handleToggleOpen}
          aria-label="Open calendar"
        >
          <svg
            aria-hidden="true"
            className={styles.icon}
            viewBox="0 0 24 24"
            focusable="false"
          >
            <rect
              x="3"
              y="5"
              width="18"
              height="16"
              rx="2"
              ry="2"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <line
              x1="3"
              y1="9"
              x2="21"
              y2="9"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <line
              x1="8"
              y1="3"
              x2="8"
              y2="7"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <line
              x1="16"
              y1="3"
              x2="16"
              y2="7"
              stroke="currentColor"
              strokeWidth="1.5"
            />
          </svg>
        </button>
      </div>

      {open && (
        <div
          ref={popoverRef}
          className={styles.popover}
          style={{
            position: 'fixed',
            top: popoverStyle.top,
            left: popoverStyle.left,
          }}
        >
          <div className={styles.monthHeader}>
            <button
              type="button"
              className={styles.navButton}
              onClick={handlePrev}
              aria-label="Previous"
            >
              ‹
            </button>
            <div className={styles.labelGroup}>
              {view !== 'month' && (
                <button
                  type="button"
                  className={styles.monthButton}
                  onClick={() => setView('month')}
                >
                  {monthName}
                </button>
              )}
              {view !== 'year' && (
                <button
                  type="button"
                  className={styles.yearButton}
                  onClick={() => setView('year')}
                >
                  {yearNumber}
                </button>
              )}
            </div>
            <button
              type="button"
              className={styles.navButton}
              onClick={handleNext}
              aria-label="Next"
            >
              ›
            </button>
          </div>

          {view === 'day' && (
            <>
              <div className={styles.weekdays}>
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
                  <span key={day} className={styles.weekday}>
                    {day}
                  </span>
                ))}
              </div>

              <div className={styles.grid}>
                {days.map((date) => {
                  const isDisabled =
                    parsedMinDate &&
                    date < parsedMinDate &&
                    date.getMonth() === visibleMonth.getMonth();

                  const isCurrentMonth =
                    date.getMonth() === visibleMonth.getMonth();
                  const isToday = date.toDateString() === today.toDateString();
                  const isSelected =
                    parsedValue &&
                    date.toDateString() === parsedValue.toDateString();

                  const dayClasses = [
                    styles.day,
                    !isCurrentMonth ? styles.dayMuted : '',
                    isToday ? styles.dayToday : '',
                    isSelected ? styles.daySelected : '',
                    isDisabled ? styles.dayDisabled : '',
                  ]
                    .filter(Boolean)
                    .join(' ');

                  return (
                    <button
                      type="button"
                      key={date.toISOString()}
                      className={dayClasses}
                      onClick={() => handleSelectDate(date)}
                      disabled={!!isDisabled}
                    >
                      {date.getDate()}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {view === 'month' && (
            <div className={styles.monthGrid}>
              {[
                'Jan',
                'Feb',
                'Mar',
                'Apr',
                'May',
                'Jun',
                'Jul',
                'Aug',
                'Sep',
                'Oct',
                'Nov',
                'Dec',
              ].map((label, index) => {
                const monthDate = new Date(
                  visibleMonth.getFullYear(),
                  index,
                  1,
                );
                const isSelected =
                  parsedValue &&
                  parsedValue.getFullYear() === visibleMonth.getFullYear() &&
                  parsedValue.getMonth() === index;
                const isCurrentMonth =
                  visibleMonth.getMonth() === index &&
                  today.getFullYear() === visibleMonth.getFullYear() &&
                  today.getMonth() === index;

                const classes = [
                  styles.monthCell,
                  isSelected ? styles.monthCellSelected : '',
                  isCurrentMonth ? styles.monthCellCurrent : '',
                ]
                  .filter(Boolean)
                  .join(' ');

                return (
                  <button
                    key={label}
                    type="button"
                    className={classes}
                    onClick={() => {
                      setVisibleMonth(monthDate);
                      setView('day');
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}

          {view === 'year' && (
            <div className={styles.yearGrid}>
              {years.map((year) => {
                const isSelected =
                  parsedValue && parsedValue.getFullYear() === year;
                const isCurrentYear = today.getFullYear() === year;

                const classes = [
                  styles.yearCell,
                  isSelected ? styles.yearCellSelected : '',
                  isCurrentYear ? styles.yearCellCurrent : '',
                ]
                  .filter(Boolean)
                  .join(' ');

                return (
                  <button
                    key={year}
                    type="button"
                    className={classes}
                    onClick={() => {
                      setVisibleMonth(
                        (prev) => new Date(year, prev.getMonth(), 1),
                      );
                      setView('month');
                    }}
                  >
                    {year}
                  </button>
                );
              })}
            </div>
          )}

          {showActions && (
            <div className={styles.popoverActions}>
              <button
                type="button"
                className={styles.actionButton}
                onClick={handleToday}
              >
                Today
              </button>
              <button
                type="button"
                className={styles.actionButton}
                onClick={handleClear}
              >
                Clear
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
